import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { getTodayLocalIsoDate } from "@/core/utils/func/format-date";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { SimulateKycCaseExpiredRequest } from "@/features/notification/api/dto";
import { useSimulateKycCaseExpired } from "@/features/notification/api/use-simulate-kyc-case-expired";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  createSimulateKycExpiredSchema,
  type SimulateKycExpiredFormValues,
} from "@/features/notification/schemas/simulate-event.schema";
import { PREFERRED_LANGUAGES } from "@/features/shared/api/enums";
import { usePartySelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: SimulateKycExpiredFormValues = {
  eventId: "",
  partyId: "",
  recipientAddress: "",
  preferredLanguage: "FR",
  level: "TIER1",
  expiryDate: "",
};

const freshValues = (): SimulateKycExpiredFormValues => ({
  ...EMPTY_VALUES,
  eventId: crypto.randomUUID(),
  expiryDate: getTodayLocalIsoDate(),
});

const toPayload = (
  values: SimulateKycExpiredFormValues
): SimulateKycCaseExpiredRequest => ({
  eventId: values.eventId.trim(),
  partyId: values.partyId.trim(),
  recipientAddress: values.recipientAddress.trim(),
  preferredLanguage: values.preferredLanguage,
  level: values.level.trim(),
  expiryDate: values.expiryDate.trim(),
});

/**
 * Dialog `simulate-kyc-case-expired` — notification d'expiration KYC.
 * `partyId` : select searchable. `eventId` : UUID client (pas de liste).
 */
export function SimulateKycExpiredDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tNotif } = useNotificationsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createSimulateKycExpiredSchema(), [i18n.language]);

  const simulate = useSimulateKycCaseExpired();
  const pending = simulate.isPending;
  const [preview, setPreview] = useState<SimulateKycCaseExpiredRequest | null>(
    null
  );
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  useEffect(() => {
    if (!open) {
      setPreview(null);
      form.reset(EMPTY_VALUES);
      return;
    }
    form.reset(freshValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé à l'ouverture.
  }, [open]);

  const fieldError =
    (name: keyof SimulateKycExpiredFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const handleConfirm = () => {
    if (!preview) return;
    simulate.mutate(preview, {
      onSuccess: () => {
        notifSilence(tNotif("notifications.events.feedback.sent"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{tNotif("notifications.events.kycExpired.title")}</DialogTitle>
            <DialogDescription>
              {tNotif("notifications.events.warning")}
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="eventId" validators={{ onChange: fieldError("eventId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ke-eventId">
                    {tNotif("notifications.events.common.eventId")}
                  </Label>
                  <Input
                    id="ke-eventId"
                    value={field.state.value}
                    placeholder={tNotif("notifications.events.common.eventIdPlaceholder")}
                    className="notranslate font-mono"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="partyId" validators={{ onChange: fieldError("partyId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ke-partyId">
                    {tNotif("notifications.events.common.partyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="ke-partyId"
                    options={partyOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif("notifications.events.common.partyIdPlaceholder")}
                    isLoading={partiesLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="recipientAddress"
              validators={{ onChange: fieldError("recipientAddress") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="ke-recipient">
                    {tNotif("notifications.events.common.recipientAddress")}
                  </Label>
                  <Input
                    id="ke-recipient"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.common.recipientAddressPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="preferredLanguage"
              validators={{ onChange: fieldError("preferredLanguage") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{tNotif("notifications.events.common.preferredLanguage")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as SimulateKycExpiredFormValues["preferredLanguage"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFERRED_LANGUAGES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="level" validators={{ onChange: fieldError("level") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="ke-level">
                    {tNotif("notifications.events.kycExpired.level")}
                  </Label>
                  <Input
                    id="ke-level"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.kycExpired.levelPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="expiryDate"
              validators={{ onChange: fieldError("expiryDate") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="ke-expiry">
                    {tNotif("notifications.events.kycExpired.expiryDate")}
                  </Label>
                  <Input
                    id="ke-expiry"
                    type="date"
                    value={field.state.value}
                    className="notranslate"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {tNotif("notifications.events.confirm.action")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={tNotif("notifications.events.confirm.title")}
        description={tNotif("notifications.events.confirm.description")}
        t={tNotif}
        confirmLabel={tNotif("notifications.events.confirm.action")}
        pending={pending}
        onConfirm={handleConfirm}
      />
    </>
  );
}
